/**
 * @fileoverview Input/output schemas for storyline-expander-v1.
 *
 * Expands a SINGLE storyline by adding, modifying, or splitting beats,
 * enriching its summary, and generating story questions.
 * Supports "bring to standard" and "go beyond standard" modes.
 *
 * Output is flat (3 levels max) for Gemini API compatibility.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import { StructurePhaseSchema } from '@/domains/story-planning/schemas';

// =============================================================================
// STORYLINE TARGET METADATA (passed from hook)
// =============================================================================

const StorylineTargetSchema = z.object({
  name: z.string().describe('Storyline name (for prompt context).'),
  priority: z.string().describe('A, B, or C priority.'),
  currentBeats: z.number().describe('Current number of beats.'),
  targetBeats: z.number().describe('Target number of beats for this mode.'),
  currentQuestions: z.number().describe('Current number of story questions.'),
  targetQuestions: z.number().describe('Target number of story questions for this mode.'),
  hasSummary: z.boolean().describe('Whether the storyline has a summary.'),
});

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.characters.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapters.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.worldElements.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.userMessage.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.planningGenreGuidance.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.projectedWordCount.schema.shape,
  // The single storyline to expand (structured object, rendered by <<<storylineData>>> macro)
  storylineData: z.any().describe(
    'The single storyline object to expand, including its beats, summary, and story questions.'
  ),
  // Mode and target metadata calculated by hook
  expansionMode: z.enum(['bring-to-standard', 'go-beyond-standard']).describe(
    'Which expansion mode to use. "bring-to-standard" fills a sparse storyline to baseline. ' +
    '"go-beyond-standard" extends an already-adequate storyline by ~50%.'
  ),
  isBringToStandard: z.boolean().describe('Convenience flag: true when mode is bring-to-standard.'),
  storylineTarget: StorylineTargetSchema.describe(
    'Target metrics for this storyline, calculated by the hook.'
  ),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * A beat in the output — either preserved, modified, or new.
 * If `id` is present the beat existed before (preserved or modified).
 * If `id` is absent the beat is new (consumer will generate an ID).
 */
const OutputBeatSchema = z.object({
  id: z.string().optional().describe(
    'If this beat existed before, include its original ID. Omit for new beats.'
  ),
  globalPhase: StructurePhaseSchema.describe('Where this beat sits in the overall story timeline.'),
  storylinePhase: StructurePhaseSchema.describe(
    'The structural function this beat serves for this storyline\'s arc.'
  ),
  order: z.number().min(0).describe(
    'Ordering index (integer >= 0) within this globalPhase.'
  ),
  plotEvent: z.string().describe(
    'The concrete, observable event. Focus on what physically happens.'
  ),
  narrativeState: z.string().optional().describe(
    'The emotional/thematic state at this moment (optional).'
  ),
  goalProgress: z.number().min(-1).max(1).describe(
    'Progress toward the storyline goal at this moment (-1 to +1).'
  ),
});

/**
 * A question in the output — either preserved or new.
 * If `id` is present the question existed before.
 */
const OutputQuestionSchema = z.object({
  id: z.string().optional().describe(
    'If this question existed before, include its original ID. Omit for new questions.'
  ),
  question: z.string().describe('The dramatic question text.'),
  status: z.enum(['open', 'answered']).optional().describe(
    'Preserve original status for existing questions. Omit for new questions (defaults to open).'
  ),
});

export const OutputSchema = z.object({
  summary: z.string().describe('Enriched summary (3-4 sentences minimum).'),
  beats: z.array(OutputBeatSchema).describe(
    'The full set of beats for this storyline after expansion — existing (preserved or modified) and new combined.'
  ),
  questions: z.array(OutputQuestionSchema).describe(
    'The full set of story questions after expansion — existing and new combined.'
  ),
  commentary: z.string().describe(
    'Brief summary of what was expanded and why (2-4 sentences).'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
